/**
 * The individual samples behind one week's statistics.
 *
 * Every sample in the week appears — including ones missing a spec, which is
 * what makes the panels' per-spec n reconcilable against this table. A missing
 * spec renders as an em dash, visually distinct from a zero: "not measured" and
 * "measured at 0" are different statements, and this report's whole premise is
 * not confusing them.
 *
 * Rows arrive keyed through `shared/rows.ts` because the payload is positional
 * (§8.6). `Event ID` comes from one event stream per site here, so it is a safe
 * row key within the single-site scope every view of this dataset has.
 */

import { useMemo, useState } from 'react'

import { displayText } from '../shared/format'
import type { Keyed } from '../shared/rows'
import { count, text } from '../shared/rows'

import { fmtSpec, formatDateTime } from './format'

type SortKey = 'collection_time' | 'event_id' | 'product_number'

const SPEC_CELLS: { key: string; decimals: number }[] = [
  { key: 'brix', decimals: 2 },
  { key: 'acid', decimals: 3 },
  { key: 'ratio', decimals: 2 },
  { key: 'color', decimals: 1 },
  { key: 'pulp', decimals: 2 },
]

function specValue(cell: unknown): number | null {
  return typeof cell === 'number' ? cell : null
}

/**
 * Whether a value breaks its own row's band, and the band text for the title.
 * Judged against the limits the sample was tested against — never against the
 * formula's current ones — so history stays honest across spec revisions. A
 * missing bound constrains nothing.
 */
function bandCheck(
  row: Keyed,
  key: string,
  value: number,
  decimals: number,
): { out: boolean; band: string } | null {
  const low = specValue(row[`${key}_low`])
  const high = specValue(row[`${key}_high`])
  if (low === null && high === null) return null
  const out = (low !== null && value < low) || (high !== null && value > high)
  return { out, band: `${fmtSpec(low, decimals)}–${fmtSpec(high, decimals)}` }
}

export function SampleTable({ rows }: { rows: readonly Keyed[] }) {
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: 'collection_time',
    asc: false,
  })

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a[sort.key] ?? ''
      const bv = b[sort.key] ?? ''
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true })
      return sort.asc ? cmp : -cmp
    })
    return copy
  }, [rows, sort])

  if (rows.length === 0) {
    return <div className="state">No samples recorded for this week.</div>
  }

  const header = (key: SortKey, label: string) => (
    <th
      className="sortable"
      onClick={() => {
        setSort({ key, asc: sort.key === key ? !sort.asc : true })
      }}
      aria-sort={sort.key === key ? (sort.asc ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sort.key === key && (sort.asc ? ' ↑' : ' ↓')}
    </th>
  )

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="detail-table fpo-table">
        <colgroup>
          <col style={{ width: 90 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 90 }} />
          <col style={{ minWidth: 260 }} />
          <col style={{ width: 100 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 90 }} />
        </colgroup>
        <thead>
          <tr>
            {header('event_id', 'Event ID')}
            {header('collection_time', 'Collected')}
            {header('product_number', 'Product #')}
            <th>Product</th>
            <th>Blend lot</th>
            <th className="cell-num-head">Brix</th>
            <th className="cell-num-head">Acid</th>
            <th className="cell-num-head">Brix/Acid</th>
            <th className="cell-num-head">Color</th>
            <th className="cell-num-head">Pulp</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const eventId = count(row.event_id)
            return (
              <tr key={eventId} className="detail-row">
                <td className="cell-mono">{eventId}</td>
                <td className="cell-mono">{formatDateTime(text(row.collection_time))}</td>
                <td className="cell-mono">{displayText(text(row.product_number))}</td>
                <td className="cell-truncate" title={displayText(text(row.product_name))}>
                  {displayText(text(row.product_name))}
                </td>
                <td className="cell-truncate">{displayText(text(row.blend_lot))}</td>
                {SPEC_CELLS.map(({ key, decimals }) => {
                  const value = specValue(row[key])
                  if (value === null) {
                    return (
                      <td key={key} className="cell-mono cell-num">
                        <span className="fpo-missing" title="Not measured">
                          —
                        </span>
                      </td>
                    )
                  }
                  const check = bandCheck(row, key, value, decimals)
                  if (check?.out === true) {
                    return (
                      <td key={key} className="cell-mono cell-num">
                        <span className="fpo-oos" title={`Outside spec ${check.band}`}>
                          {fmtSpec(value, decimals)}
                        </span>
                      </td>
                    )
                  }
                  return (
                    <td key={key} className="cell-mono cell-num">
                      {check === null ? (
                        fmtSpec(value, decimals)
                      ) : (
                        <span title={`Spec ${check.band}`}>{fmtSpec(value, decimals)}</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
